"use client"

import { cn } from "@/lib/utils"
import {
  LayoutDashboard,
  BookOpen,
  Search,
  Settings,
  Archive,
  Users,
  Truck,
  Bell,
  FileText,
  Building2,
  Megaphone,
  FileSearch,
  Shield,
  User,
} from "lucide-react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useAuth } from "@/lib/auth-context"

const mainNavItems = [
  {
    label: "ダッシュボード",
    href: "/",
    icon: LayoutDashboard,
  },
  {
    label: "帳票閲覧",
    href: "/ledger",
    icon: BookOpen,
  },
  {
    label: "得意先元帳",
    href: "/customer-ledger",
    icon: Users,
  },
  {
    label: "仕入先元帳",
    href: "/supplier-ledger",
    icon: Truck,
  },
  {
    label: "検索",
    href: "/search",
    icon: Search,
  },
  {
    label: "お知らせ",
    href: "/notifications",
    icon: Bell,
  },
]

const adminNavItems = [
  {
    label: "操作ログ",
    href: "/operation-logs",
    icon: FileText,
  },
  {
    label: "SSO設定",
    href: "/sso-settings",
    icon: Shield,
  },
  {
    label: "設定",
    href: "/settings",
    icon: Settings,
  },
]

const superAdminNavItems = [
  {
    label: "テナント管理",
    href: "/admin/tenants",
    icon: Building2,
  },
  {
    label: "監査ログ",
    href: "/admin/audit-logs",
    icon: FileSearch,
  },
  {
    label: "お知らせ管理",
    href: "/admin/announcements",
    icon: Megaphone,
  },
]

export function Sidebar() {
  const pathname = usePathname()
  const { user } = useAuth()

  const isAdmin = user?.role === "admin" || user?.role === "super_admin"
  const isSuperAdmin = user?.role === "super_admin"

  const renderNavItem = (item: { label: string; href: string; icon: typeof LayoutDashboard }) => {
    const isActive = pathname === item.href || (item.href !== "/" && pathname.startsWith(item.href))
    return (
      <Link
        key={item.href}
        href={item.href}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
        )}
      >
        <item.icon className="h-5 w-5" />
        {item.label}
      </Link>
    )
  }

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-64 bg-sidebar text-sidebar-foreground">
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 border-b border-sidebar-border px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sidebar-primary">
            <Archive className="h-5 w-5 text-sidebar-primary-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold tracking-tight">Bugyo Archive</span>
            <span className="text-xs text-sidebar-foreground/70">Cloud</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-1">
            {mainNavItems.map(renderNavItem)}
          </div>

          {isAdmin && (
            <>
              <div className="my-3 border-t border-sidebar-border" />
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                管理
              </p>
              <div className="space-y-1">
                {adminNavItems.map(renderNavItem)}
              </div>
            </>
          )}

          {isSuperAdmin && (
            <>
              <div className="my-3 border-t border-sidebar-border" />
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                システム管理
              </p>
              <div className="space-y-1">
                {superAdminNavItems.map(renderNavItem)}
              </div>
            </>
          )}
        </nav>

        {/* User & Footer */}
        <div className="border-t border-sidebar-border px-3 py-3">
          <Link
            href="/profile"
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
              pathname === "/profile"
                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                : "text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
            )}
          >
            <User className="h-4 w-4" />
            <span className="truncate">{user?.name || user?.email || "ユーザー設定"}</span>
          </Link>
          <p className="mt-2 px-3 text-xs text-sidebar-foreground/60">
            &copy; 2026 Bugyo Archive Cloud
          </p>
        </div>
      </div>
    </aside>
  )
}
